import { Component } from "react";
import { Card, ButtonGroup, ToggleButton, Row, Col, Table, Badge } from "react-bootstrap";
import AceView from "./aceView";
import { GrDocument } from "react-icons/gr";
import { LuTableProperties, LuComponent } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import Tooltip from "../../../controls/common/tooltip";
import Clipboard from "../../../controls/common/clipboard";
import WidgetStatus from "./widgetStatus";
import { MarkdownBody } from "./markdown";
import Utils from "context/utils";
import "./index.css";

export class ContentItemText extends Component {
  constructor(props) {
    super(props);

    this.state = {
      viewOption: this.props.contentView || "raw"
    };

    this.viewOptions = [
      { label: "Raw", value: "raw" },
      { label: "Markdown", value: "markdown" },
      { label: "JSON", value: "json" }
    ];
  }

  componentDidUpdate(prevProps) {
    if ( this.props.contentView !== prevProps.contentView ) {
      this.setState({ viewOption: this.props.contentView });
    }
  }

  renderRaw() {
    return (
      <AceView
        name="playground-widget-message-content-item"
        width="100%"
        mode="text"
        theme="monokai"
        readOnly="true"
        wrapEnabled="true"
        value={this.props.contentItem.text}
        minLines={1}
        maxLines={30}
      />
    );
  }

  renderMarkdown() {
    return <MarkdownBody text={this.props.contentItem.text} />;
  }

  renderJson() {
    let text;
    try {
      text = JSON.stringify(JSON.parse(this.props.contentItem.text), null, 2);
    } catch ( error ) {
      return (
        <WidgetStatus variant="warning">
          Content item contains invalid JSON.
        </WidgetStatus>
      );
    }

    return (
      <AceView
        name="playground-widget-message-content-item"
        width="100%"
        mode="json"
        theme="monokai"
        readOnly="true"
        wrapEnabled="true"
        value={text}
        minLines={3}
        maxLines={30}
      />
    );
  }

  render() {
    if (typeof this.props.contentItem.text !== "string" && !(this.props.contentItem.text instanceof String)) {
      return (
        <WidgetStatus variant="warning">
          Content item text is not a string.
        </WidgetStatus>
      );
    }

    return (
      <div className="playground-message-content-item">
        <div className={"raw" === this.state.viewOption ? "" : "hidden"}>
          {this.renderRaw()}
        </div>
        <div className={"markdown" === this.state.viewOption ? "" : "hidden"}>
          {this.renderMarkdown()}
        </div>
        <div className={"json" === this.state.viewOption ? "" : "hidden"}>
          {this.renderJson()}
        </div>

        <span className="playground-message-content-item-controls">
          <ButtonGroup toggle size="sm">
            {this.viewOptions.map((option, index) => (
              <ToggleButton
                key={index}
                type="radio"
                variant="light"
                name="radio"
                value={option.value}
                checked={this.state.viewOption === option.value}
                onChange={(event) => { this.setState({ viewOption: event.currentTarget.value }) }}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ButtonGroup>
          <Clipboard text={this.props.contentItem.text}/>
        </span>
      </div>
    );
  }
}

export default class Message extends Component {
  constructor(props) {
    super(props);

    this.state = {
      viewOption: "content"
    };

    this.viewOptions = [
      { label: <GrDocument/>, value: "content", tooltip: "Content" },
      { label: <LuTableProperties/>, value: "attributes", tooltip: "Attributes" },
      { label: <LuComponent/>, value: "providers", tooltip: "Providers" },
      { label: <CgDetailsMore/>, value: "metadata", tooltip: "Metadata" },
    ];
  }

  renderContent() {
    if ( !this.props.value.message.content ) {
      return (
        <WidgetStatus variant="light">
          Message content is not specified.
        </WidgetStatus>
      );
    }

    return (
      <Table bordered hover className="playground-message-content">
        <tbody>
          {this.props.value.message.content.map((contentItem, index) => (
            <tr>
              <td>
                {"text" === contentItem.type &&
                  <ContentItemText
                    contentItem={contentItem}
                    contentView={this.props.contentView}
                  />
                }
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderMessageField(field) {
    if ( !this.props.value.message[field] ) {
      return (
        <WidgetStatus variant="light">
          Message "{field}" field is not specified.
        </WidgetStatus>
      );
    }
    return (
      <>
        <Card.Text>
          <AceView
            name="playground-widget-message-attributes"
            width="100%"
            mode="json"
            theme="monokai"
            readOnly="true"
            wrapEnabled="true"
            value={JSON.stringify(this.props.value.message[field], null, 2)}
            minLines={3}
            maxLines={30}
          />
        </Card.Text>
      </>
    );
  }

  render() {
    if ( !this.props.value ) {
      return (
        <WidgetStatus variant="warning">
          Message is not provided.
        </WidgetStatus>
      );
    }
    const message = this.props.value["message"];
    if ( !message ) {
      return (
        <WidgetStatus variant="warning">
          Message contains no value.
        </WidgetStatus>
      );
    }

    return (
      <div>
        {/* Role + timestamp on the left, view toggles on the right, always on ONE line.
            This was a Bootstrap Row of two Cols with a float-right control group: below the `sm`
            breakpoint the Cols stacked, so in a narrow widget the long timestamp wrapped onto a second
            line and the toggles dropped underneath it, colliding with the content below. A plain flex
            row with a shrinkable left side and a fixed-size right side holds the layout at any width —
            the timestamp ellipsizes instead, keeping its full value in the title. */}
        <div className="playground-message-meta">
          <div className="playground-message-meta-left">
            <Badge variant="secondary" className="title-tag-badge">{message["role"]}</Badge>
            {(message["attributes"] && message["attributes"]["created_at"]) &&
              <span
                className="text-muted playground-message-timestamp"
                title={Utils.formatDateTime(message["attributes"]["created_at"], this.props.timezone)}
              >
                {Utils.formatCompactDateTime(message["attributes"]["created_at"], this.props.timezone)}
              </span>
            }
          </div>
          <ButtonGroup toggle size="sm" className="playground-message-meta-right">
            {this.viewOptions.map((option, index) => (
              <Tooltip text={option.tooltip}>
                <ToggleButton
                  key={index}
                  type="radio"
                  variant="light"
                  name="radio"
                  value={option.value}
                  checked={this.state.viewOption === option.value}
                  onChange={(event) => { this.setState({ viewOption: event.currentTarget.value }) }}
                >
                  {option.label}
                </ToggleButton>
              </Tooltip>
            ))}
          </ButtonGroup>
        </div>

        <div className={"content" == this.state.viewOption ? "" : "hidden"}>
          {this.renderContent()}
        </div>
        <div className={"attributes" == this.state.viewOption ? "" : "hidden"}>
          <Table bordered hover className="playground-message-content">
            <tbody><tr><td>
              {this.renderMessageField("attributes")}
            </td></tr></tbody>
          </Table>
        </div>
        <div className={"providers" == this.state.viewOption ? "" : "hidden"}>
          <Table bordered hover className="playground-message-content">
            <tbody><tr><td>
              {this.renderMessageField("providers")}
            </td></tr></tbody>
          </Table>
        </div>
        <div className={"metadata" == this.state.viewOption ? "" : "hidden"}>
          <Table bordered hover className="playground-message-content">
            <tbody><tr><td>
              {this.renderMessageField("metadata")}
            </td></tr></tbody>
          </Table>
        </div>
      </div>
    );
  }
}
